import { CSSProperties } from 'react';
import { Id, ToastContent, ToastOptions, toast } from 'react-toastify';
import { Icon } from '@fuxi/eevee-icon';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { TOAST_CONTAINER_ID } from '@/constant';

class Toastify {
  static ToastLoadingPool = [];

  loading<TData>(content: ToastContent<TData>, options?: Omit<ToastOptions, 'containerId'>) {
    const id = toast.warn(content, {
      className: 'immersion-toastify',
      containerId: TOAST_CONTAINER_ID.ImmersionMode,
      icon: <Loading3QuartersOutlined spin style={{ fontSize: '18px' }} />,
      autoClose: false,
      ...options,
    });

    Toastify.ToastLoadingPool.push(id);

    return {
      id,
      clear: () => this.clear(id),
    };
  }

  warn<TData>(content: ToastContent<TData>, options?: ToastOptions<{}> & { iconProps?: CSSProperties }) {
    const { icon, iconProps, ...restProps } = options || {};

    toast.warn(content, {
      className: 'immersion-toastify',
      containerId: TOAST_CONTAINER_ID.ImmersionMode,
      icon:
        typeof icon === 'string' ? (
          <Icon
            name={icon}
            style={{
              position: 'relative',
              top: '1px',
              fontSize: '32px',
              ...iconProps,
            }}
          />
        ) : (
          icon
        ),
      autoClose: 2000,
      ...restProps,
    });
  }

  clear(id?: Id) {
    toast.dismiss(id);
    const loadingPool = Toastify.ToastLoadingPool;
    if (id) {
      loadingPool.splice(
        loadingPool.findIndex(item => item === id),
        1
      );
    } else {
      Toastify.ToastLoadingPool = [];
    }
  }

  /** L3级别Toast */
  public l3 = {
    instance: this as Toastify,
    /** 机器人网络有点问题，请稍等 */
    networkException() {
      return this.instance.loading('机器人网络有点问题，请稍等');
    },
    /** 机器人已断开连接，正在恢复中，请稍等 */
    disconnect() {
      return this.instance.loading('机器人已断开连接，正在恢复中，请稍等');
    },
    // TODO:下方兼容人体异常检测1.6.0-1.6.5之间的逻辑
    /** 趴姿不正确 */
    incorrectPronePosture() {
      return this.instance.loading('请引导用户调整位置，处于屏幕中央');
    },
    /** 人体未趴平 */
    notProneFlat() {
      return this.instance.loading('检测到顾客未趴平，请引导顾客调整姿势');
    },
    /** 有遮挡物 */
    hasObstruction() {
      return this.instance.loading('请移除理疗区域中的遮挡物');
    },
    /** 人体一直运动 */
    oftenKeepMoving() {
      return this.instance.loading('请引导顾客趴好并保持静止');
    },
    /** 腹部射频被手遮挡 */
    handBlockMassageArea() {
      return this.instance.loading('请勿用手遮挡待理疗区域');
    },
    /** 请确保理疗区域衣物平整无褶皱，头发无遮挡，且光照均匀 */
    avoidSunlight() {
      return this.instance.loading('请确保理疗区域衣物平整无褶皱，头发无遮挡，且光照均匀。');
    },
    /** 人体检测中... */
    humanDetecting() {
      return this.instance.loading('人体检测中...');
    },
  };

  /** L4级别Toast */
  public l4 = {
    instance: this as Toastify,
    /** 打击时压力不足 */
    lowForce() {
      this.instance.warn('打击时压力不足', { icon: 'low-force' });
    },
    /** 击打时部分线路跳过 */
    partLineSkip() {
      this.instance.warn('击打时部分线路跳过', {
        icon: 'shockwave',
        iconProps: { fontSize: '24px' },
      });
    },
    /** 磁震波硬件异常，无法开始开枪 */
    shockHardwareException() {
      this.instance.warn('磁震波硬件异常，无法开始开枪', {
        icon: 'shockwave-color',
        iconProps: { fontSize: '24px', color: '#FF3325' },
      });
    },
    /** 内源热能能量调节失败 */
    rfAdjustFail() {
      this.instance.warn('内源热能能量调节失败', {
        icon: 'radio-freq',
        iconProps: { color: '#f3af3e', fontSize: '24px', marginRight: '4px' },
      });
    },
    /** 服务中断网 */
    offline() {
      this.instance.warn('服务中断网', { icon: 'wifi-offline', iconProps: { color: '#fff', fontSize: '24px' } });
    },
    cameraBlocked() {
      this.instance.warn('检测到摄像头被遮挡', {
        icon: 'camera',
        iconProps: { color: '#4A4A4A', fontSize: '24px', marginTop: '-4px' },
      });
    },
    // 冲击波流程服务中滑落风险提示
    swSlippingWarning() {
      this.instance.warn('当前穴区下压力度较大，机械臂会有滑落风险', {
        icon: 'sw-hit',
        iconProps: { color: '#4A4A4A', fontSize: '24px', marginTop: '-4px' },
      });
    },
    // 按摩流程服务中滑落风险提示
    massageSlippingWarning() {
      this.instance.warn('当前穴区下压力度较大，机械臂会有滑落风险', {
        icon: 'massage-hit',
        iconProps: { color: '#4A4A4A', fontSize: '24px', marginTop: '-4px' },
      });
    },
    showRFTherapyTip() {
      this.instance.warn('为保障理疗效果，请及时为顾客补涂射频膏哦～', {
        icon: 'heart',
        iconProps: { color: '#FD3E3E', fontSize: '22px', marginTop: '-4px' },
        autoClose: 5000,
      });
    },
  };
}

export default new Toastify();
